import { useState } from 'react'
import { useProgramContent, useProgramContentMutations } from '@/api/queries'
import { Button } from '@/components/ui/Button'
import { ConfirmDialog, Drawer } from '@/components/ui/Overlay'
import { useToast } from '@/components/ui/Toast'
import type { SlateProgram } from '@/types'
import { ProgramContentEditor } from './ProgramContentEditor'

/**
 * A slate program's content (PC-9, ADR-0018 § 10). It shows its WB program's,
 * read only and marked as inherited, until a copywriter gives it its own,
 * blank or copied; its own content is edited and approved here, and dropping
 * it returns the slate program to inheriting.
 */
export function SlateContentDrawer({
  clientId,
  slate,
  onClose,
  onOpenWbProgram,
}: {
  clientId: string
  slate: SlateProgram | null
  onClose: () => void
  /** Opens the WB program drawer, where inherited content is edited. */
  onOpenWbProgram: (wbProgramId: string) => void
}) {
  if (!slate) return null
  return (
    <SlateContent key={slate.id} clientId={clientId} slate={slate} onClose={onClose} onOpenWbProgram={onOpenWbProgram} />
  )
}

function SlateContent({
  clientId,
  slate,
  onClose,
  onOpenWbProgram,
}: {
  clientId: string
  slate: SlateProgram
  onClose: () => void
  onOpenWbProgram: (wbProgramId: string) => void
}) {
  const { data } = useProgramContent(clientId, 'slate_program', slate.id)
  const ops = useProgramContentMutations(clientId, 'slate_program', slate.id)
  const toast = useToast()
  const [confirmDrop, setConfirmDrop] = useState(false)
  const wb = data?.inheritsFrom ?? null

  async function create(copy: boolean) {
    try {
      await ops.createOwn.mutateAsync(copy)
      toast.push(copy ? `Copied ${wb?.name}'s content.` : 'Started its own content.')
    } catch (e) {
      toast.push(e instanceof Error ? e.message : 'Could not create the content.', 'error')
    }
  }

  return (
    <>
      <Drawer
        open
        onClose={onClose}
        title={slate.slateName}
        subtitle={<span className="text-ink-faint">{slate.enabled ? 'Slate program' : 'Slate program · Disabled'}</span>}
      >
        {!data ? (
          <p className="text-[12px] text-ink-faint">Loading content…</p>
        ) : (
          <div className="space-y-4">
            {data.own ? (
              <div className="flex flex-wrap items-center gap-2 rounded-[6px] border border-rule bg-surface-sunk px-3 py-2 text-[12px]">
                <span className="flex-1">
                  Its own content{wb ? `, not ${wb.name}'s` : ''}.
                </span>
                <Button size="sm" variant="ghost" onClick={() => setConfirmDrop(true)}>
                  {wb ? `Use ${wb.name}'s content` : 'Remove its content'}
                </Button>
              </div>
            ) : (
              <div className="space-y-2 rounded-[6px] border border-rule bg-surface-sunk px-3 py-2 text-[12px]">
                <p>
                  {wb ? (
                    <>
                      Inherited from <span className="font-semibold">{wb.name}</span>: edit it there, or give this
                      slate program its own.
                    </>
                  ) : (
                    'Not mapped to a WB program, so it has no content to inherit.'
                  )}
                </p>
                <div className="flex flex-wrap gap-2">
                  {wb && data.content && (
                    <Button size="sm" variant="primary" onClick={() => void create(true)} loading={ops.createOwn.isPending}>
                      Write its own, starting from {wb.name}'s
                    </Button>
                  )}
                  <Button size="sm" onClick={() => void create(false)} loading={ops.createOwn.isPending}>
                    Write its own, blank
                  </Button>
                  {wb && (
                    <Button size="sm" variant="quiet" onClick={() => onOpenWbProgram(wb.id)}>
                      Open {wb.name}
                    </Button>
                  )}
                </div>
              </div>
            )}
            {(data.own || data.content) && (
              <ProgramContentEditor clientId={clientId} owner="slate_program" ownerId={slate.id} readOnly={!data.own} />
            )}
          </div>
        )}
      </Drawer>

      <ConfirmDialog
        open={confirmDrop}
        onClose={() => setConfirmDrop(false)}
        onConfirm={async () => {
          try {
            await ops.removeOwn.mutateAsync()
          } catch {
            toast.push('Could not remove its content.', 'error')
            return
          }
          setConfirmDrop(false)
          toast.push(wb ? `Now shows ${wb.name}'s content.` : 'Removed its content.')
        }}
        title={wb ? `Use ${wb.name}'s content instead?` : 'Remove its content?'}
        description="This slate program's own content is deleted."
        confirmLabel={wb ? 'Use inherited content' : 'Remove content'}
        destructive
        busy={ops.removeOwn.isPending}
      />
    </>
  )
}
